<i18n>
{
    "en_US": {
        "confirm-delete": "Delete?",
        "delete-warning": "You will not be able to recover it after...",
        "no": "No",
        "no-note-yet": "You don't have any note yet...",
        "no-note-found": "We couldn't find any note. Let me try to improve your search:",
        "no-more-results": "No more results to show",
        "no-tags": "No tags",
        "no-title": "_(No title)_",
        "why-not-create-one": "Why not create one?",
        "yes": "Yes"
    },
    "fr_FR": {
        "confirm-delete": "Supprimer?",
        "delete-warning": "Vous ne pourrez plus la récupérer après...",
        "no": "Non",
        "no-note-yet": "Vous n'avez pas encore de note...",
        "no-note-found": "Aucune note trouvée. Essayons d'améliorer votre recherche:",
        "no-more-results": "Aucun autre résultat disponible",
        "no-tags": "Aucun tag",
        "no-title": "_(Aucun titre)_",
        "why-not-create-one": "Pourquoi ne pas en créer une ?",
        "yes": "Oui"
    }
}
</i18n>

<template>
    <div class="scrollbar-container">
        <loader-basic-spinner v-if="busy === true" class="blue large" />
        <div v-if="suggestions.length > 0 && busy === false">
            <div class="suggestions text-center">
                <p class="text-center none">
                    {{$t("no-note-found")}}<br />
                </p>
                <a class="suggestion btn btn-link" v-bind:key="suggestion.word" v-for="suggestion in suggestions" v-on:click="setSearch(suggestion.word)">
                    {{suggestion.word}}
                </a>
            </div>
        </div>
        <ul v-if="notes.length > 0 && busy === false">
            <li class="note" v-for="note in notes" v-bind:key="note.id" v-on:click="activate" v-bind:data-id="note.id" v-bind:class="{active: isCurrentlyActive(note.id)}">
                <i class="fa fa-trash-alt" v-on:click.prevent.stop="trash"></i>
                <h4 class="heading mt-0" v-html="markdownTitle(note)"></h4>
                <p class="text" v-html="summarize(note)"></p>
                <div class="info">
                    <i class="fa fa-folder" v-on:click.prevent="trash"></i>
                    <span class="category" v-html="joinedTags(note)" v-bind:title="joinedFullTags(note)"></span>
                    <span class="time" v-bind:data-timestamp="timestamp(note)" v-bind:title="timestamp(note) | moment('LLLL')">
                        {{ timestamp(note) | moment("from", "now") }}
                    </span>
                </div>
            </li>
            <infinite-loading @infinite="infiniteHandler" :distance="100" ref="infiniteLoading">
                <div slot="no-results">
                    <span class="end-of-results" v-text="$t('no-more-results')"></span>
                </div>
                <div slot="no-more">
                    <span class="end-of-results" v-text="$t('no-more-results')"></span>
                </div>
            </infinite-loading>
        </ul>
        <p class="text-center none" v-if="notes.length === 0 && suggestions.length === 0 && busy === false">
            {{$t("no-note-yet")}}<br />
            <br />
            {{$t("why-not-create-one")}}
        </p>
    </div>
</template>

<script>
    import InfiniteLoading from "vue-infinite-loading";
    import LoaderBasicSpinner from "loaders/BasicSpinner";
    import { mapState, mapMutations, mapActions } from "vuex";

    export default {
        name: "Notes",
        data: function() {
            return {
                busy: false,
                interval: null
            };
        },
        computed: {
            ...mapState("search", ["suggestions", "search"]),
            ...mapState("note", ["notes"])
        },
        watch: {
            search: function() {
                if (this.$refs.infiniteLoading) {
                    this.$refs.infiniteLoading.$emit("$InfiniteLoading:reset");
                }
                // The fact the infinite loading may not be
                // always available can create trouble...
                setTimeout(function() {
                    if (this.$refs.infiniteLoading) {
                        this.$refs.infiniteLoading.$emit("$InfiniteLoading:reset");
                    }
                }.bind(this), 200);
            }
        },
        components: {
            LoaderBasicSpinner,
            InfiniteLoading
        },
        methods: {
            markdownTitle: function(note) {
                if (note.title === "_(no title)_") {
                    return marked(this.$t("no-title"), { sanitize: true });
                }
                return marked(note.title, { sanitize: true });
            },
            summarize: function(note) {
                if (!note.content) {
                    return note.content;
                }
                const p = document.createElement("p");
                const d = document.querySelector(".scrollbar-container");
                p.style.visibility = "hidden";
                p.style.position = "absolute";
                p.style.top = "-1000px";
                p.style.fontFamily = "Roboto, sans-serif";
                p.style.fontSize = "14px";
                p.style.fontWeight = "300";
                p.style.textAlign = "justify";
                p.style.minHeight = "21px";
                p.style.maxWidth = "243px";
                d.appendChild(p);
                const s = note.content.split(/\s/gi);

                if (!Array.isArray(s) || s.length < 1) {
                    return note.content;
                }

                let lastTmp = "";
                let tmp = "";
                for (let i = 0, l = s.length; i < l; i++) {
                    if (i < l - 1) {
                        tmp = s.slice(0, i).join(" ") + " (...)";
                    } else {
                        tmp = s.join(" ");
                    }

                    p.innerHTML = tmp;
                    if (p.clientHeight > 21) {
                        break;
                    }
                    lastTmp = tmp;
                }

                d.removeChild(p);

                return lastTmp;
            },
            timestamp: function(note) {
                return note.updated_at || note.created_at;
            },
            joinedTags: function(note) {
                if (Array.isArray(note.tags) && note.tags.length > 0) {
                    var length = 0,
                        results = [];
                    for (let i = 0, l = note.tags.length; i < l && length < 14; i++) {
                        if (note.tags[i]) {
                            results.push(note.tags[i]);
                            length += note.tags[i].length;
                        }
                    }
                    if (results.length != note.tags.length) {
                        results.push("...");
                    }
                    return results.join(", ");
                }
                return this.$t("no-tags");
            },
            joinedFullTags: function(note) {
                if (Array.isArray(note.tags) && note.tags.length > 0) {
                    return note.tags.join(", ");
                }
                return this.$t("no-tags");
            },
            isCurrentlyActive: function(id) {
                return (this.$route.name === "home.filled-note-editor" && this.$route.params.note_id == id);
            },
            activate: function(event) {
                // Getting element
                let el = $(event.target);
                if (!el.hasClass("note")) {
                    el = el.closest(".note");
                }

                // Moving to new route
                this.$router.push({
                    name: "home.filled-note-editor",
                    params: {
                        note_id: el.attr("data-id")
                    }
                });

                // Hiding menu
                this.hideMenuMobile();
            },
            trash: function(event) {
                // Getting element
                let el = $(event.target);
                if (!el.hasClass("note")) {
                    el = el.closest(".note");
                }

                swal({
                    title: this.$t("confirm-delete"),
                    text: this.$t("delete-warning"),
                    showCancelButton: true,
                    confirmButtonClass: "btn-danger",
                    confirmButtonText: this.$t("yes"),
                    cancelButtonText: this.$t("no"),
                    closeOnConfirm: true,
                    closeOnCancel: true
                }, function(isConfirm) {
                    if (isConfirm) {
                        this.deleteNote(el.attr("data-id"))
                        .then(function() {
                            this.$Progress.finish();
                        }.bind(this))
                        .catch(function() {});
                    }
                }.bind(this));
            },
            infiniteHandler: function($state) {
                this.loadNotes()
                .then(function(results) {
                    if (results.data.content.length < 20) {
                        $state.complete();
                    } else {
                        $state.loaded();
                    }
                    this.$Progress.finish();
                }.bind(this))
                .catch(function() {
                    // We stop it before it's going to wrong...
                    $state.complete();
                });
            },
            refreshNoteTimestamp: function() {
                const moment = this.$moment;
                $(this.$el).find(".time").each(function(index, el) {
                    const that = $(el);
                    that.html(moment(that.attr("data-timestamp")).fromNow());
                });
            },
            ...mapActions("note", ["loadNotes", "reloadNotes", "deleteNote"]),
            ...mapMutations("application", ["hideMenuMobile"]),
            ...mapMutations("search", ["setSearch"])
        },
        mounted: function() {
            this.busy = true;
            this.reloadNotes()
            .then(function() {
                this.busy = false;
                this.$Progress.finish();
            }.bind(this))
            .catch(function() {});
            this.interval = setInterval(this.refreshNoteTimestamp.bind(this), 5000);
        },
        beforeDestroy: function() {
            clearInterval(this.interval);
            this.interval = null;
        }
    }
</script>

<style scoped>
    .scrollbar-container {
        overflow: hidden;
        height: calc(100% - 112px);
        max-height: 100%;
    }
    ul {
        padding: 0;
        overflow: auto;
        height: 100%;
    }
    p.none {
        font-size: 0.75em;
        color: #666;
        margin-top: 20px;
    }
    li {
        cursor: pointer;
        font-size: 14px;
        line-height: 1.42857143;
        color: #333;
        position: relative;
        display: block;
        margin-bottom: -1px;
        background-color: #fff;
        border: 1px solid #ddd;
        padding: 13px 20px;
        border-bottom: 1px solid rgb(228, 234, 236);
        border-right: 0;
        border-radius: 0px !important;
    }
    li:first-child {
        border-top: 0;
    }
    .fa-trash-alt {
        position: absolute;
        right: 10px;
        top: 17px;
        -ms-filter:"progid:DXImageTransform.Microsoft.Alpha(Opacity=0)";
        filter: alpha(opacity=0);
        opacity: 0;

        -webkit-transition: opacity 0.2s ease-in-out;
        -moz-transition: opacity 0.2s ease-in-out;
        -ms-transition: opacity 0.2s ease-in-out;
        -o-transition: opacity 0.2s ease-in-out;
        transition: opacity 0.2s ease-in-out;
    }
    li:hover .fa-trash-alt {
        -ms-filter:"progid:DXImageTransform.Microsoft.Alpha(Opacity=100)";
        filter: alpha(opacity=100);
        opacity: 100;
    }
    h4.heading {
        color: #76838f;
        text-shadow: none;
        font-size: 14px;
        font-weight: 300;
        line-height: 21px;
        text-align: justify;
    }
    p.text {
        text-shadow: none;
        font-size: 14px;
        font-weight: 300;
        line-height: 21px;
        min-height: 21px;
        margin-bottom: 15px;
        text-align: justify;
        text-overflow: ellipsis;
        white-space: normal;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        color: #76838f !important;
    }
    span.category {
        color: #a3afb7;
        text-shadow: none;
        font-weight: 100;
        font-size: 12px;
        line-height: 14px;
        float: left;
    }
    span.time {
        color: #a3afb7;
        text-shadow: none;
        float: right;
        font-size: 12px;
        font-weight: 100;
    }
    span.time:after, .info:after {
        clear: both;
    }
    .info {
        padding-bottom: 15px;
    }
    .fa {
        color: #a3afb7;
        margin-right: 5px;
        float: left;
    }

    /* Hover */
    li:hover {
        background: rgb(239, 246, 255) !important;
    }

    /* Active */
    li.active {
        background: rgb(217, 233, 255) !important;
    }
    li.active h4.heading, li.active p.text {
        color: #4d545a;
    }
    li.active span.category, li.active span.time, li.active .fa {
        color: #6d7377;
    }

    .end-of-results {
        color: #a3afb7;
        font-weight: 100;
        font-size: 10px;
    }

    .suggestions {
        width: 70%;
        margin: 0 auto;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-around;
    }

    .suggestions h5 {
        display: block;
    }

    .suggestion {
        cursor: pointer;
    }

    /* Spinner */
    .scrollbar-container >>> .loader-basic-spinner {
        display: block;
        margin: 0 auto;
        margin-top: 50px;
    }
</style>